import {Button, Group, Modal, Stack, TextInput} from '@mantine/core'
import {useForm} from '@mantine/form'
import {useEffect, useState} from 'react'

import type {
    CreateThirdPartyRentalCompanyInput,
    ThirdPartyRentalCompany,
    UpdateThirdPartyRentalCompanyInput,
} from './schemas'

type ThirdPartyRentalCompanyFormValues = {
    name: string
}

type ThirdPartyRentalCompanyFormModalProps = {
    opened: boolean
    company: ThirdPartyRentalCompany | null
    onClose: () => void
    onCreate: (input: CreateThirdPartyRentalCompanyInput) => Promise<ThirdPartyRentalCompany>
    onUpdate: (
        id: string,
        input: UpdateThirdPartyRentalCompanyInput,
    ) => Promise<ThirdPartyRentalCompany>
    onSaved: () => void
}

export function ThirdPartyRentalCompanyFormModal({
    opened,
    company,
    onClose,
    onCreate,
    onUpdate,
    onSaved,
}: ThirdPartyRentalCompanyFormModalProps) {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const isEditing = company != null

    const form = useForm<ThirdPartyRentalCompanyFormValues>({
        initialValues: {name: ''},
        validate: {
            name: (value) => (value.trim() ? null : 'Company name is required'),
        },
    })

    useEffect(() => {
        if (!opened) {
            return
        }
        form.setValues({name: company?.name ?? ''})
        form.clearErrors()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reset modal fields when opened
    }, [opened, company])

    function handleClose() {
        form.reset()
        onClose()
    }

    async function handleSubmit(values: ThirdPartyRentalCompanyFormValues) {
        setIsSubmitting(true)
        try {
            const input = {name: values.name.trim()}
            if (isEditing && company) {
                await onUpdate(company.id, input)
            } else {
                await onCreate(input)
            }
            onSaved()
            form.reset()
            onClose()
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={isEditing ? 'Edit company' : 'Create company'}
            centered
        >
            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap="md">
                    <TextInput label="Name" withAsterisk {...form.getInputProps('name')} />

                    <Group justify="flex-end">
                        <Button type="button" variant="default" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button type="submit" loading={isSubmitting}>
                            {isEditing ? 'Save changes' : 'Create company'}
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Modal>
    )
}
